import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Camera, Image as ImageIcon, Upload, AlertCircle, Loader, Phone, User, X, CheckCircle2, Info, ImagePlus, IndianRupee, Star, Shield, Clock, MessageCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function SellCollectible() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    seller_name: '',
    seller_phone: '',
  });
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (selectedImages.length + files.length > 3) {
      setError('Maximum 3 images allowed');
      return;
    }

    const isDuplicate = files.some(newFile => 
      selectedImages.some(existingFile => 
        existingFile.name === newFile.name && 
        existingFile.size === newFile.size
      )
    );

    if (isDuplicate) {
      setError('Please do not upload the same image multiple times');
      return;
    }

    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Each image must be less than 5MB');
      return;
    }

    const newPreviews = await Promise.all(
      files.map(file => new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      }))
    );

    setSelectedImages(prev => [...prev, ...files]);
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    setError(null);

    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (selectedImages.length === 0) {
      setError('Please select at least one image of your coin/note');
      return;
    }

    try {
      setIsSubmitting(true);

      const phone = formData.seller_phone.startsWith('+91') 
        ? formData.seller_phone 
        : `+91${formData.seller_phone}`;

      const uploadPromises = selectedImages.map(async (image, index) => {
        const fileName = `${Date.now()}-${index}-${image.name}`;
        const { error: uploadError } = await supabase.storage
          .from('collectible-images')
          .upload(`seller-submissions/${fileName}`, image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('collectible-images')
          .getPublicUrl(`seller-submissions/${fileName}`);

        return publicUrl;
      });

      const imageUrls = await Promise.all(uploadPromises);

      const { error: submissionError } = await supabase
        .from('seller_submissions')
        .insert({
          seller_name: formData.seller_name,
          seller_phone: phone,
          image_url: imageUrls[0],
          status: 'pending',
          admin_notes: imageUrls.length > 1 ? `Additional images: ${imageUrls.slice(1).join(', ')}` : null
        });

      if (submissionError) throw submissionError;

      setSuccess(true);
      setFormData({
        seller_name: '',
        seller_phone: ''
      });
      setSelectedImages([]);
      setPreviewUrls([]);

      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sell Your Old Coins & Currency Notes | Best Price Guaranteed - OldCoinage.com</title>
        <meta name="description" content="Get the best price for your old coins and currency notes. Expert evaluation, instant quotes, and secure transactions. Sell your rare coins, British India coins, ancient coins, and collectible notes today." />
        <meta name="keywords" content="sell old coins, sell rare coins, sell ancient coins, sell British India coins, coin buyer near me, sell currency notes, coin collection buyer, numismatic buyer, sell coin collection, coin dealer" />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
            <div className="relative">
              <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-100">
                <Upload className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary-400 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                Sell Your Coins & Notes
              </h1>
              <p className="text-gray-500">Get the best value for your numismatic treasures</p>
            </div>
          </div>

          <div className="mb-8 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm text-blue-800">
                <p className="font-medium">Important Instructions:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Upload up to 3 clear images of your coin/note</li>
                  <li>Include both sides (obverse and reverse) in separate photos</li>
                  <li>Take clear, well-lit photos that show all details</li>
                  <li>Avoid using flash as it can cause glare</li>
                  <li>Place the item against a plain, dark background</li>
                  <li>Maximum file size: 5MB per image</li>
                </ul>
                <p className="pt-2 text-blue-700">
                  Our experts will evaluate your submission and contact you within 24-48 hours.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <div>
                  <p className="font-medium">Submission successful!</p>
                  <p className="text-sm">We will contact you soon to discuss your item.</p>
                  <p className="text-sm mt-1">Redirecting to home page...</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="seller_name" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="seller_name"
                  name="seller_name"
                  value={formData.seller_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, seller_name: e.target.value }))}
                  required
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  placeholder="Enter your full name"
                  disabled={success}
                />
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="seller_phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  id="seller_phone"
                  name="seller_phone"
                  value={formData.seller_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, seller_phone: e.target.value }))}
                  required
                  pattern="^(\+91)?[0-9]{10}$"
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  placeholder="Enter your 10-digit mobile number"
                  disabled={success}
                />
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Format: +91XXXXXXXXXX or 10 digits
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Images
                </label>
                <span className="text-sm text-gray-500">
                  {selectedImages.length}/3 images
                </span>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleImageSelect}
                disabled={success || selectedImages.length >= 3}
              />
              
              <input
                type="file"
                ref={cameraInputRef}
                className="hidden"
                accept="image/*"
                capture="environment"
                onChange={handleImageSelect}
                disabled={success || selectedImages.length >= 3}
              />

              <div className="grid grid-cols-2 gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={success || selectedImages.length >= 3}
                  className="aspect-square flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600">Use Camera</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={success || selectedImages.length >= 3}
                  className="aspect-square flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600">Upload Image</span>
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    {!success && (
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-full">
                      Image {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={success}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || selectedImages.length === 0 || success}
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit for Evaluation'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* SEO Content Section */}
        <div className="mt-12 space-y-12">
          <section className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Sell Your Old Coins & Notes - Get the Best Value
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <IndianRupee className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Best Market Price</h3>
                    <p className="text-sm text-gray-600">Get top value for your collection</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Expert Evaluation</h3>
                    <p className="text-sm text-gray-600">Professional assessment by numismatists</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Clock className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Quick Process</h3>
                    <p className="text-sm text-gray-600">Fast evaluation and payment</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Star className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Premium Service</h3>
                    <p className="text-sm text-gray-600">Trusted by thousands of collectors</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Expert Consultation</h3>
                    <p className="text-sm text-gray-600">Free advice on your collection</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Secure Transaction</h3>
                    <p className="text-sm text-gray-600">Safe and transparent process</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-primary max-w-none">
              <h3>We Buy All Types of Old Coins and Currency Notes</h3>
              <ul>
                <li>Ancient Indian Coins (Punch-marked, Kushan, Gupta period)</li>
                <li>British India Coins and Currency Notes</li>
                <li>East India Company Coins</li>
                <li>Republic India Rare Coins</li>
                <li>Commemorative Coins</li>
                <li>Old Currency Notes</li>
                <li>Complete Coin Collections</li>
                <li>Rare and Valuable Coins</li>
              </ul>

              <h3>Why Choose Us to Sell Your Coins?</h3>
              <p>
                With over 15 years of experience in numismatics, we offer the most competitive prices for your old coins and currency notes. Our team of expert numismatists provides accurate evaluations based on authenticity, rarity, condition, and historical significance.
              </p>

              <h3>How It Works</h3>
              <ol>
                <li>Upload clear photos of your coins or notes</li>
                <li>Receive a free evaluation within 24-48 hours</li>
                <li>Accept our competitive offer</li>
                <li>Get paid securely and promptly</li>
              </ol>

              <h3>What We Look For</h3>
              <ul>
                <li>Rare and ancient coins from all periods</li>
                <li>British India coins and notes</li>
                <li>Pre-independence currency</li>
                <li>Commemorative issues</li>
                <li>Complete collections</li>
                <li>Inherited coin collections</li>
                <li>Investment-grade coins</li>
              </ul>

              <h3>Fair and Transparent Process</h3>
              <p>
                We believe in complete transparency. Our evaluation process is thorough and professional, ensuring you get the best value for your numismatic items. 
              </p>

              <h3>Expert Evaluation Criteria</h3>
              <ul>
                <li>Historical significance and rarity</li>
                <li>Condition and preservation</li>
                <li>Mint marks and variations</li>
                <li>Market demand and trends</li>
                <li>Authentication and certification</li>
              </ul>

              <div className="bg-primary-50 p-6 rounded-xl mt-8">
                <h3 className="text-primary-800">Ready to Sell Your Coins?</h3>
                <p className="text-primary-700">
                  Don't let your valuable coins and notes collect dust. Get them evaluated by our experts and receive the best market price. Upload photos of your items today or contact us for a free consultation.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900">How do I know if my coins are valuable?</h3>
                <p className="text-gray-600 mt-2">
                  The value of coins depends on factors like age, rarity, condition, and historical significance. Our experts can help evaluate your coins and provide a detailed assessment of their worth.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900">What types of coins do you buy?</h3>
                <p className="text-gray-600 mt-2">
                  We buy all types of old coins and currency notes, including ancient Indian coins, British India coins, Republic India rare coins, commemorative issues, and complete collections.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900">How long does the evaluation process take?</h3>
                <p className="text-gray-600 mt-2">
                  Our expert team typically provides evaluations within 24-48 hours of receiving clear photos of your items. For rare or complex collections, it might take slightly longer to ensure accurate assessment.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900">Is the evaluation free?</h3>
                <p className="text-gray-600 mt-2">
                  Yes, we provide free evaluations for all coins and currency notes. There's no obligation to sell after receiving our assessment.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900">How do I get paid?</h3>
                <p className="text-gray-600 mt-2">
                  We offer secure payment methods including bank transfer, UPI, or cash payment. The payment is processed promptly once you accept our offer.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}